import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox as BrCheckbox, Fieldset, Layout } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { MaskedTextFormField, masks } from '../../../../../components/FormFields/MaskedTextFormField';
import { NumericFormField } from '../../../../../components/FormFields/NumericFormField';
import { VehicleType } from '../../../../../resources/base/models/vehicleTypes';
import { isEngineElectric, isEngineElectricOrHybrid } from '../../../../../services/common/miscUtils';
import { VehicleConfigData } from '../../../../../types';
import { Datepicker, Dropdown } from '../../fieldset';
import {
  checkCurrentMileageStockAndNew,
  checkCurrentRegDateStock,
  getEngineList,
  getVehicleList,
  modelYearList,
} from './utils';

export const StockVehicleBaseForm: FunctionComponent<{
  manufacturersList: {
    value: string;
    label: string;
  }[];
  allDataForAllVehicles: Array<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>;
  vehicleGroupModelsList: {
    value: string;
    label: string;
    id: string;
  }[];
  setShowDemoCarModal: React.Dispatch<React.SetStateAction<boolean>>;
  isFormReadonly: boolean;
  onVehicleModelSelect?: (model: { label: string; carPrice?: number }) => void;
}> = ({
  manufacturersList,
  allDataForAllVehicles,
  vehicleGroupModelsList,
  setShowDemoCarModal,
  isFormReadonly,
  onVehicleModelSelect,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<VehicleConfigData>();
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(true);

  useEffect(() => {
    if (manufacturersList.length === 0) {
      return;
    }

    if (manufacturersList.length === 1) {
      setFieldValue('brand', manufacturersList[0].value);
      setIsDropdownDisabled(true);
    } else {
      setIsDropdownDisabled(false);
    }
  }, [isDropdownDisabled, manufacturersList, setFieldValue]);

  useEffect(() => {
    const modelYears = modelYearList(allDataForAllVehicles);
    const engines = getEngineList(values?.model, allDataForAllVehicles);
    const vehicles = getVehicleList(values?.model, values?.engine, allDataForAllVehicles);
    if (modelYears.length === 1) {
      setFieldValue('model', modelYears[0].value);
    }

    if (engines.length === 1) {
      setFieldValue('engine', engines[0].value);
    }

    if (vehicles.length === 1) {
      setFieldValue('vehicleMilesId', vehicles[0].value);
      const modelNameWithoutPrice = vehicles[0].label.split(' [')[0];
      setFieldValue('modelName', modelNameWithoutPrice);
    }
  }, [allDataForAllVehicles, setFieldValue, values?.engine, values?.model]);

  useEffect(() => {
    if (vehicleGroupModelsList.length === 1) {
      setFieldValue('modelGroup', vehicleGroupModelsList[0].value);
    }
  }, [setFieldValue, vehicleGroupModelsList]);

  return (
    <Fieldset>
      <Fieldset>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/4" s="1/1">
              <Dropdown
                name="brand"
                label={t('step1:configVehicle:brand')}
                list={manufacturersList}
                noPlaceholder={false}
                carTypeStep1={VehicleType.STOCK}
                disabled={isFormReadonly || isDropdownDisabled}
              />
            </Layout.Item>

            <Layout.Item default="1/4" s="1/1">
              <Dropdown
                name="modelGroup"
                label={t('step1:configVehicle:modelGroup')}
                list={vehicleGroupModelsList}
                noPlaceholder={false}
                disabled={isFormReadonly}
                onValueChange={() => {
                  setFieldValue('vehicleMilesId', '');
                  setFieldTouched('vehicleMilesId', false);
                }}
              />
            </Layout.Item>

            <Layout.Item default="1/4" s="1/1">
              <Dropdown
                name="model"
                label={t('step1:configVehicle:model')}
                list={modelYearList(allDataForAllVehicles)}
                noPlaceholder={false}
                disabled={isFormReadonly}
              />
            </Layout.Item>

            <Layout.Item default="1/4" s="1/1">
              <Dropdown
                name="engine"
                label={t('step1:configVehicle:engine')}
                list={getEngineList(values?.model, allDataForAllVehicles)}
                noPlaceholder={false}
                disabled={isFormReadonly}
              />
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1" s="1/1">
              <Dropdown
                name="vehicleMilesId"
                label={t('step1:configVehicle:modelName')}
                list={getVehicleList(values?.model, values?.engine, allDataForAllVehicles)}
                disabled={isFormReadonly}
                onValueChange={(label: string, carPrice) => {
                  onVehicleModelSelect?.({ label, carPrice });
                  setFieldValue('modelName', label);
                  setFieldValue('modelDescription', label);
                }}
              />
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" s="1/1">
            <MaskedTextFormField
              mask={masks.vinMask}
              fieldName="vin"
              labelTranslationKey="step1:configVehicle:vin"
              isDisabled={isFormReadonly}
              overwrite
              removeWhitespace
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <NumericFormField
              fieldName="batteryPrice"
              type="money"
              maximumFractionDigits={2}
              labelTranslationKey="step1:configVehicle:batteryPrice"
              optional={!isEngineElectricOrHybrid(values?.engine)}
              isDisabled={!isEngineElectricOrHybrid(values?.engine) || isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <NumericFormField
              fieldName="co2emision"
              labelTranslationKey={t('step1:configVehicle:co2emission')}
              type="emission"
              isDisabled={isEngineElectric(values?.engine) || isFormReadonly}
              maximumFractionDigits={0}
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/3" s="1/1">
            <BrCheckbox
              checked={values?.isDemo}
              name="demoCar"
              disabled={isFormReadonly}
              onChange={(e) => {
                setFieldValue('isDemo', e.target.checked);
              }}
            >
              {t('step1:configVehicle:demoCarCheckbox')}
            </BrCheckbox>
          </Layout.Item>

          {values.isDemo && (
            <Layout.Item default="2/3" s="1/1">
              <Fieldset>
                <Fieldset.Row>
                  <Layout>
                    <Layout.Item default="1/2" l="1/2" s="1/1">
                      <Datepicker
                        name="regDate"
                        label={t('step1:configVehicle:regDate')}
                        infoText={t('step1:dateInfoText:label')}
                        value={values.regDate}
                        setFieldValue={setFieldValue}
                        maxDate={new Date().setHours(0, 0, 0, 0)}
                        onValueChange={(regDate) => checkCurrentRegDateStock(regDate, setShowDemoCarModal)}
                        disabled={isFormReadonly}
                      />
                    </Layout.Item>
                    <Layout.Item default="1/2" l="1/2" s="1/1">
                      <NumericFormField
                        fieldName="currMileage"
                        labelTranslationKey={t('step1:configVehicle:currMileage')}
                        type="km"
                        maximumFractionDigits={0}
                        onValueChange={(currMileage: number) =>
                          checkCurrentMileageStockAndNew(currMileage, setShowDemoCarModal)
                        }
                        allowEmpty
                        isDisabled={isFormReadonly}
                      />
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>
              </Fieldset>
            </Layout.Item>
          )}
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
